// Member Library - read-only, org-wide knowledge base browse + reader

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contentAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { hasGuestPrivileges, isLeadershipRole } from '../utils/roles';
import {
  Search, Book, X, FileText, Youtube, Globe, Sparkles, Download, GraduationCap,
  Play, ClipboardList, Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import type { Teaching } from '../types';
import { getYouTubeId, youtubeEmbedSrc, youtubeWatchUrl } from '../lib/youtube';
function SourceIcon({ type, size = 18 }: { type: Teaching['source_type']; size?: number }) {
  if (type === 'youtube') return <Youtube size={size} className="text-red-500" />;
  if (type === 'url') return <Globe size={size} className="text-blue-500" />;
  if (type === 'pdf') return <FileText size={size} className="text-gray-500" />;
  return <Book size={size} className="text-navy" />;
}

// Thumbnail "card" background used when a document has no derivable image
// (everything except YouTube). Keeps the source color-coding from the list
// icons so the grid stays visually consistent.
const FALLBACK_THUMB_STYLES: Record<Teaching['source_type'], string> = {
  youtube: 'from-red-500 to-red-700',
  url: 'from-blue-500 to-blue-700',
  pdf: 'from-slate-500 to-slate-700',
  text: 'from-navy to-navy-dark',
  markdown: 'from-navy to-navy-dark',
  video: 'from-purple-500 to-purple-700',
};

function FallbackThumbnailIcon({ type }: { type: Teaching['source_type'] }) {
  const iconProps = { size: 40, className: 'text-white/85', strokeWidth: 1.75 };
  if (type === 'youtube' || type === 'video') return <Youtube {...iconProps} />;
  if (type === 'url') return <Globe {...iconProps} />;
  if (type === 'pdf') return <FileText {...iconProps} />;
  return <Book {...iconProps} />;
}

// "June 2026" style caption date, per the stakeholder's requested look.
function monthYearLabel(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function youtubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

// File-backed teaching media is served from authenticated API endpoints that
// bare <video>/<img> tags can't reach (they can't send the JWT header, so the
// request 401s). Detect those URLs and swap in the real storage URL, fetched
// with an authenticated request. Resolved URLs are cached briefly — R2 signed
// URLs expire after an hour, so don't cache them for longer.
const PROTECTED_MEDIA_RE = /\/api\/content\/teachings\/\d+\/(?:short-video|full-video|image)\//;
const MEDIA_URL_CACHE_TTL_MS = 30 * 60 * 1000;
const mediaUrlCache = new Map<string, { url: string; at: number }>();

function useResolvedMediaUrl(url: string | undefined): string | undefined {
  const [resolved, setResolved] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!url || !PROTECTED_MEDIA_RE.test(url)) {
      setResolved(url || undefined);
      return;
    }
    const cached = mediaUrlCache.get(url);
    if (cached && Date.now() - cached.at < MEDIA_URL_CACHE_TTL_MS) {
      setResolved(cached.url);
      return;
    }
    let cancelled = false;
    setResolved(undefined);
    contentAPI
      .resolveMediaUrl(url)
      .then((realUrl) => {
        mediaUrlCache.set(url, { url: realUrl, at: Date.now() });
        if (!cancelled) setResolved(realUrl);
      })
      .catch(() => {
        if (!cancelled) setResolved(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return resolved;
}

// Square library-card thumbnail: a real preview image for YouTube-sourced
// teachings (derived from the video id, no API key needed) or a color-coded
// icon card for everything else, with the title/month superimposed like a
// content-library tile.
function Thumbnail({ teaching }: { teaching: Teaching }) {
  const videoId = teachingVideoId(teaching);
  // Prefer the real content date; fall back to the import date.
  const monthYear = monthYearLabel(teaching.content_date || teaching.created_at);
  // Thumbnail priority: re-hosted hero image → YouTube preview → gradient card.
  // Kept as an ordered candidate list so a load failure (e.g. a broken hero
  // URL) advances to the next candidate instead of showing a broken image.
  const heroImageUrl = useResolvedMediaUrl(teaching.hero_image_url || undefined);
  const candidates = useMemo(() => {
    const list: string[] = [];
    if (heroImageUrl) list.push(heroImageUrl);
    if (videoId) list.push(youtubeThumbnailUrl(videoId));
    return list;
  }, [heroImageUrl, videoId]);
  const [failedSources, setFailedSources] = useState<Set<string>>(() => new Set());
  const imageUrl = candidates.find((candidate) => !failedSources.has(candidate));

  return (
    <div className="relative aspect-square w-full overflow-hidden bg-navy">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={() => {
            setFailedSources((current) => new Set(current).add(imageUrl));
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${FALLBACK_THUMB_STYLES[teaching.source_type] ?? FALLBACK_THUMB_STYLES.text}`}
        >
          <FallbackThumbnailIcon type={teaching.source_type} />
        </div>
      )}
      {videoId && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm">
            <Play size={20} fill="currentColor" />
          </span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pb-2.5 pt-8">
        {monthYear && (
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/75">{monthYear}</p>
        )}
        <p className="text-sm font-semibold text-white line-clamp-2">{teaching.title}</p>
      </div>
      <span className="absolute top-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm">
        <SourceIcon type={teaching.source_type} size={14} />
      </span>
    </div>
  );
}


function teachingVideoId(t: Teaching): string | null {
  return getYouTubeId(t.original_url) ?? getYouTubeId(t.source_url);
}

type LibraryTab = 'all' | 'facilitator';

export default function Library() {
  const { user } = useAuth();
  const [teachings, setTeachings] = useState<Teaching[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Results of an explicit "Search by meaning" run; null = browsing the full
  // list (with the instant client-side filter applied as you type).
  const [semanticResults, setSemanticResults] = useState<Teaching[] | null>(null);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Teaching | null>(null);
  const [videoVariant, setVideoVariant] = useState<'short' | 'full'>('short');
  const [activeTab, setActiveTab] = useState<LibraryTab>('all');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const canSeeFacilitatorTab = isLeadershipRole(user?.role);
  const isGuest = hasGuestPrivileges(user);

  useEffect(() => {
    fetchAll();
  }, []);

  // Deep link: /library?doc=<id> opens that document directly (used by Jarvis
  // citations and "open the full teaching" links).
  useEffect(() => {
    const docId = searchParams.get('doc');
    if (!docId) return;
    let cancelled = false;
    (async () => {
      try {
        const full = await contentAPI.getTeaching(Number(docId));
        if (!cancelled) setSelected(full);
      } catch {
        // Not found or not visible to this member — ignore and show the list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const closeReader = () => {
    setSelected(null);
    if (searchParams.get('doc')) {
      searchParams.delete('doc');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const data = await contentAPI.getTeachings();
      setTeachings(data);
    } catch (error) {
      console.error('Error loading library:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Plain text search filters the already-loaded list instantly as you type;
  // only "Search by meaning" (semantic/RAG) needs a server round trip.
  const handleSemanticSearch = async () => {
    const q = searchQuery.trim();
    if (!q || semanticLoading) return;
    setSemanticLoading(true);
    // Ask the backend to filter to this tab's content up front, so a
    // facilitator-library search doesn't return org/forum matches that then
    // get silently dropped and look like "no results".
    const visibility = activeTab === 'facilitator' ? 'admins_only' : undefined;
    try {
      const data = await contentAPI.semanticSearch(q, 20, undefined, visibility);
      setSemanticResults(data);
    } catch (error) {
      console.error('Error searching library:', error);
      toast.error('Something went wrong searching the library. Please try again.');
    } finally {
      setSemanticLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSemanticResults(null);
    setCategoryFilter(null);
  };

  const openReader = async (teaching: Teaching) => {
    // Search results may be truncated; fetch the full document for the reader.
    try {
      const full = await contentAPI.getTeaching(teaching.id);
      setSelected(full);
    } catch {
      setSelected(teaching);
    }
  };

  const isLinkedGrowGuide = (teaching: Teaching) =>
    Boolean(teaching.grow_guide_url && !teaching.grow_guide_filename);

  const handleDownload = async (
    teaching: Teaching,
    role: 'content-sheet' | 'grow-guide' = 'content-sheet',
  ) => {
    if (role === 'grow-guide' && isLinkedGrowGuide(teaching) && teaching.grow_guide_url) {
      window.open(teaching.grow_guide_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setDownloadingId(teaching.id);
    try {
      await contentAPI.downloadTeachingAsset(
        teaching.id,
        role,
        role === 'content-sheet'
          ? teaching.content_sheet_filename
          : teaching.grow_guide_filename,
      );
    } catch {
      toast.error('Could not download the file.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Facilitator-only training content lives in its own tab; the "All content"
  // tab shows exactly what regular members see.
  const tabTeachings = useMemo(() => {
    const source = semanticResults ?? teachings;
    if (activeTab === 'facilitator') {
      return source.filter((t) => t.visibility === 'admins_only');
    }
    return source.filter((t) => t.visibility !== 'admins_only');
  }, [teachings, semanticResults, activeTab]);

  // Category chips reflect the current tab's full list (not the filtered
  // view), so they don't jump around while typing.
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    teachings
      .filter((t) =>
        activeTab === 'facilitator'
          ? t.visibility === 'admins_only'
          : t.visibility !== 'admins_only',
      )
      .forEach((t) => {
        (t.categories || []).forEach((c) => counts.set(c, (counts.get(c) ?? 0) + 1));
      });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [teachings, activeTab]);

  const visibleTeachings = useMemo(() => {
    let list = tabTeachings;
    if (categoryFilter) {
      list = list.filter((t) => (t.categories || []).includes(categoryFilter));
    }
    // Instant filter while browsing; semantic results are already the answer
    // to the query, so don't text-filter them again.
    const q = searchQuery.trim().toLowerCase();
    if (q && semanticResults === null) {
      list = list.filter((t) =>
        [t.title, t.author, t.summary, ...(t.categories || [])]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(q)),
      );
    }
    return list;
  }, [tabTeachings, categoryFilter, searchQuery, semanticResults]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || categoryFilter !== null || semanticResults !== null;

  const hasVideoVariant = Boolean(selected?.short_video_url || selected?.full_video_url);
  const hasBothDirectVariants = Boolean(
    selected?.short_video_url && selected?.full_video_url,
  );
  const selectedVariantUrl = selected
    ? hasBothDirectVariants
      ? videoVariant === 'short'
        ? selected.short_video_url
        : selected.full_video_url
      : selected.short_video_url || selected.full_video_url
    : undefined;
  const selectedVariantYoutubeId = getYouTubeId(selectedVariantUrl);
  const selectedDirectVideo = selectedVariantYoutubeId ? undefined : selectedVariantUrl;
  const resolvedDirectVideo = useResolvedMediaUrl(selectedDirectVideo);
  const resolvedHeroImage = useResolvedMediaUrl(selected?.hero_image_url || undefined);
  const selectedVideoId = selected
    ? selectedVariantYoutubeId ?? (!hasVideoVariant ? teachingVideoId(selected) : null)
    : null;
  const selectedLegacyVideo = selected && !hasVideoVariant ? selected.video_url : undefined;

  useEffect(() => {
    setVideoVariant('short');
  }, [selected?.id]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">Library</h1>
          <p className="text-gray-600">Browse the Iron Forums knowledge base — teachings, articles, and talks.</p>
        </div>
        {canSeeFacilitatorTab && (
          <button
            onClick={() => navigate('/admin/teachings')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition self-start"
          >
            <Plus size={18} />
            Add content
          </button>
        )}
      </div>

      {isGuest && (
        <div className="rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 text-sm text-navy">
          You can open this month’s teaching. More teachings are for members.
        </div>
      )}

      {canSeeFacilitatorTab && (
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => {
              setActiveTab('all');
              setCategoryFilter(null);
              setSemanticResults(null);
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              activeTab === 'all'
                ? 'border-navy text-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All content
          </button>
          <button
            onClick={() => {
              setActiveTab('facilitator');
              setCategoryFilter(null);
              setSemanticResults(null);
            }}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              activeTab === 'facilitator'
                ? 'border-gold text-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <GraduationCap size={16} className="text-gold" /> Facilitator library
          </button>
        </div>
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Typing again means a new intent; drop stale semantic results.
              if (semanticResults !== null) setSemanticResults(null);
            }}
            placeholder="Search the library..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
          />
        </div>
        <button
          onClick={handleSemanticSearch}
          disabled={!searchQuery.trim() || semanticLoading}
          title="Finds teachings by meaning, not just exact words"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gold text-gold rounded-lg hover:bg-gold hover:text-white transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gold"
        >
          <Sparkles size={16} />
          {semanticLoading ? 'Searching…' : 'Search by meaning'}
        </button>
        {hasActiveFilters && (
          <button onClick={clearSearch} className="px-4 py-2 text-gray-500 hover:text-gray-700">
            Clear
          </button>
        )}
      </div>

      {/* Category filter chips */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter(null)}
            className={`px-3 py-1.5 pointer-coarse:py-2.5 text-sm rounded-full transition ${
              categoryFilter === null
                ? 'bg-navy text-white'
                : 'bg-white text-gray-600 shadow-sm hover:text-navy'
            }`}
          >
            All
          </button>
          {categories.map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
              className={`px-3 py-1.5 pointer-coarse:py-2.5 text-sm rounded-full transition ${
                categoryFilter === cat
                  ? 'bg-navy text-white'
                  : 'bg-white text-gray-600 shadow-sm hover:text-navy'
              }`}
            >
              {cat} <span className={categoryFilter === cat ? 'text-white/70' : 'text-gray-400'}>{count}</span>
            </button>
          ))}
        </div>
      )}

      {semanticResults !== null && (
        <p className="text-sm text-gray-500">
          Showing {visibleTeachings.length} result{visibleTeachings.length === 1 ? '' : 's'} by
          meaning for “{searchQuery.trim()}”.
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      ) : visibleTeachings.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center text-gray-500">
          {hasActiveFilters ? (
            <>
              <Search className="mx-auto text-gray-300" size={48} />
              <p className="mt-2">No content matches your search.</p>
              <button
                onClick={clearSearch}
                className="mt-4 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition text-sm"
              >
                Clear search
              </button>
            </>
          ) : activeTab === 'facilitator' ? (
            <>
              <GraduationCap className="mx-auto text-gray-300" size={48} />
              <p className="mt-2">No facilitator training content yet.</p>
            </>
          ) : (
            <>
              <Book className="mx-auto text-gray-300" size={48} />
              <p className="mt-2">No teachings found.</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleTeachings.map((t) => (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => openReader(t)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openReader(t);
                }
              }}
              className="text-left bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition border border-transparent hover:border-navy/20 cursor-pointer flex flex-col"
            >
              <div className="relative">
                <Thumbnail teaching={t} />
                {t.has_content_sheet && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(t);
                    }}
                    disabled={downloadingId === t.id}
                    className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-navy shadow-sm hover:bg-white transition disabled:opacity-50"
                    title="Download Content Sheet"
                    aria-label="Download Content Sheet"
                  >
                    <Download size={14} />
                  </button>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                {t.summary && <p className="text-sm text-gray-600 line-clamp-3">{t.summary}</p>}
                <div className="flex flex-wrap gap-1 mt-3">
                  {(t.categories || []).slice(0, 3).map((cat, i) => (
                    <span key={i} className="px-2 py-0.5 bg-off-white text-navy text-xs rounded-full">{cat}</span>
                  ))}
                </div>
                {t.has_grow_guide && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(t, 'grow-guide');
                    }}
                    className="mt-3 text-sm font-medium text-gold hover:underline text-left"
                  >
                    Do the Grow Guide
                  </button>
                )}
                {t.author && <p className="text-xs text-gray-400 mt-auto pt-3">— {t.author}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reader Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-4 sticky top-0 bg-white z-10">
              <div className="flex items-start gap-2">
                <span className="mt-1"><SourceIcon type={selected.source_type} /></span>
                <div>
                  <h2 className="text-xl font-bold text-navy">{selected.title}</h2>
                  {(selected.author || monthYearLabel(selected.content_date || selected.created_at)) && (
                    <p className="text-sm text-gray-500">
                      {[selected.author, monthYearLabel(selected.content_date || selected.created_at)]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={closeReader} className="text-gray-400 hover:text-gray-600 pointer-coarse:p-2 pointer-coarse:-m-2" aria-label="Close">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {hasBothDirectVariants && (
                <div
                  className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50"
                  role="tablist"
                  aria-label="Video version"
                >
                  {(['short', 'full'] as const).map((variant) => (
                    <button
                      key={variant}
                      type="button"
                      role="tab"
                      aria-selected={videoVariant === variant}
                      onClick={() => setVideoVariant(variant)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                        videoVariant === variant
                          ? 'bg-white text-navy shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {variant === 'short' ? 'Short Video' : 'Full Video'}
                    </button>
                  ))}
                </div>
              )}
              {selectedDirectVideo ? (
                resolvedDirectVideo ? (
                  <video
                    controls
                    preload="metadata"
                    src={resolvedDirectVideo}
                    className="aspect-video w-full rounded-xl bg-black"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="aspect-video w-full rounded-xl bg-black" aria-hidden="true" />
                )
              ) : selectedVideoId ? (
                <div>
                  <iframe
                    src={youtubeEmbedSrc(selectedVideoId)}
                    title={selected.title}
                    className="aspect-video w-full rounded-xl bg-black"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                  <a
                    href={youtubeWatchUrl(selectedVideoId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex text-sm font-medium text-navy hover:underline"
                  >
                    Watch on YouTube
                  </a>
                </div>
              ) : selectedLegacyVideo ? (
                <video
                  controls
                  preload="metadata"
                  src={selectedLegacyVideo}
                  className="aspect-video w-full rounded-xl bg-black"
                >
                  Your browser does not support the video tag.
                </video>
              ) : resolvedHeroImage ? (
                <img
                  src={resolvedHeroImage}
                  alt={selected.title}
                  className="w-full rounded-xl object-cover"
                />
              ) : null}
              {(selected.categories?.length > 0 || selected.scripture_references?.length > 0) && (
                <div className="flex flex-wrap gap-1">
                  {(selected.categories || []).map((cat, i) => (
                    <span key={`c${i}`} className="px-2 py-0.5 bg-off-white text-navy text-xs rounded-full">{cat}</span>
                  ))}
                  {(selected.scripture_references || []).map((ref, i) => (
                    <span key={`s${i}`} className="px-2 py-0.5 bg-gold/10 text-gold text-xs rounded-full">{ref}</span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                {selected.has_content_sheet && (
                  <button
                    onClick={() => handleDownload(selected)}
                    disabled={downloadingId === selected.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                  >
                    <Download size={16} />
                    {downloadingId === selected.id ? 'Downloading…' : 'Download Content Sheet'}
                  </button>
                )}
                {selected.has_grow_guide && (
                  <button
                    onClick={() => handleDownload(selected, 'grow-guide')}
                    disabled={downloadingId === selected.id}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gold text-gold rounded-lg hover:bg-gold hover:text-white transition disabled:opacity-50"
                  >
                    <ClipboardList size={16} />
                    {downloadingId === selected.id ? 'Opening…' : 'Do the Grow Guide'}
                  </button>
                )}
              </div>
              {selected.summary && (
                <p className="text-[15px] leading-relaxed text-gray-600 italic border-l-2 border-gold pl-4">
                  {selected.summary}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
